// The tabbed body (Detail / Items / ASN / Communication) of
// CallOffDetailModal.tsx, split out to keep CallOffDetailModal.tsx under the
// 500-line cap. No behaviour change from the original inline JSX — moved
// verbatim.
import {
  MessageSquare, FileText, Ship,
} from 'lucide-react';
import type {
  CallOff, CallOffLine, CallOffTimelineEvent, CallOffEditHistoryEntry,
  CallOffMessage, AvailablePartForCallOff, CallOffDeliveryDestination,
} from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import CallOffLinesTable from './CallOffLinesTable';
import CallOffDetailTab from './CallOffDetailTab';
import CallOffCommunicationTab from './CallOffCommunicationTab';
import CallOffASNTab from './CallOffASNTab';

interface InlineEditChange {
  lineId: number;
  partNumber: string;
  oldQty: string;
  newQty: string;
}

interface CallOffDetailModalTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  callOff: CallOff;
  timeline: CallOffTimelineEvent[];
  editHistory: CallOffEditHistoryEntry[];
  asnTabCount: number | null;
  setAsnTabCount: (count: number | null) => void;
  messages: CallOffMessage[];
  unreadCount: number;
  inlineEditMode: boolean;
  inlineEditChanges: Record<number, InlineEditChange>;
  editingLineId: number | null;
  editingField: string | null;
  editValue: string;
  savingEdit: boolean;
  supplierCanEditLines: boolean;
  onQuantityChange: (line: CallOffLine, newQty: string) => void;
  onStartLineEdit: (lineId: number, field: string, currentValue: string) => void;
  onSaveLineEdit: () => void;
  onCancelLineEdit: () => void;
  setEditValue: (value: string) => void;
  removeLineItem: (line: CallOffLine) => void;
  showAddParts: boolean;
  availableParts: AvailablePartForCallOff[];
  availableDeliveries: CallOffDeliveryDestination[];
  selectedDelivery: string;
  setSelectedDelivery: (value: string) => void;
  addPartsLoading: boolean;
  partQuantities: Record<number, number>;
  setPartQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  addingPart: boolean;
  handleAddPart: (part: AvailablePartForCallOff) => void;
  isSupplier: boolean;
  overByPart: Record<string, { effective: number; consumed: number; over: boolean }>;
  isCustomer: boolean;
  newMessage: string;
  setNewMessage: (value: string) => void;
  handleSendMessage: () => void;
  sendingMessage: boolean;
  /** CR-7732: reload the call-off after a haulier change on the Detail tab. */
  onRefresh: () => void;
  /** CR-7732: the viewer carries this load and is party to neither side. */
  isHaulier: boolean;
  /** CR-7781: the Haulage block gained or lost unsaved edits. */
  onHaulageDirtyChange?: (dirty: boolean) => void;
}

/**
 * Renders the Detail / Items / ASN / Communication tab strip and bodies for
 * CallOffDetailModal.
 *
 * @param props - tab state plus the data and handlers each tab body needs
 * @returns the modal's tabbed content area
 */
export default function CallOffDetailModalTabs({
  activeTab, setActiveTab, callOff, timeline, editHistory, asnTabCount, setAsnTabCount,
  messages, unreadCount, inlineEditMode, inlineEditChanges, editingLineId, editingField,
  editValue, savingEdit, supplierCanEditLines, onQuantityChange, onStartLineEdit,
  onSaveLineEdit, onCancelLineEdit, setEditValue, removeLineItem, showAddParts,
  availableParts, availableDeliveries, selectedDelivery, setSelectedDelivery,
  addPartsLoading, partQuantities, setPartQuantities, addingPart, handleAddPart,
  isSupplier, overByPart, isCustomer, newMessage, setNewMessage, handleSendMessage,
  sendingMessage, onRefresh, isHaulier, onHaulageDirtyChange,
}: CallOffDetailModalTabsProps) {
  const { formatDate, formatDateTime } = useFormatters();

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="border-b px-4 pt-2">
        <TabsList className="bg-transparent border-none">
          <TabsTrigger
            value="detail"
            className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            Detail
          </TabsTrigger>
          <TabsTrigger
            value="items"
            className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            <span className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              Items ({callOff.lines?.length || 0})
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="asn"
            className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            <span className="flex items-center gap-1">
              <Ship className="h-4 w-4" />
              {/* CR-7368: the only tab here that carried no count, so a
                  load shipped on one ASN and a mixed load shipped on two
                  read identically until you opened the tab. Cancelled
                  notices are excluded server-side, matching the rule the
                  list's stage badge already applies. */}
              ASN/Shipments ({asnTabCount ?? callOff.asn_count ?? 0})
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="communication"
            className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            <span className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />
              Communication ({messages.length})
              {unreadCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {unreadCount}
                </span>
              )}
            </span>
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="detail" className="m-0 p-4">
        <CallOffDetailTab
          callOff={callOff}
          timeline={timeline}
          editHistory={editHistory}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          isCustomer={isCustomer}
          onRefresh={onRefresh}
          isHaulier={isHaulier}
          onHaulageDirtyChange={onHaulageDirtyChange}
        />
      </TabsContent>

      <TabsContent value="items" className="m-0 p-4">
        <CallOffLinesTable
          callOff={callOff}
          inlineEditMode={inlineEditMode}
          inlineEditChanges={inlineEditChanges}
          editingLineId={editingLineId}
          editingField={editingField}
          editValue={editValue}
          savingEdit={savingEdit}
          supplierCanEditLines={supplierCanEditLines}
          onQuantityChange={onQuantityChange}
          onStartLineEdit={onStartLineEdit}
          onSaveLineEdit={onSaveLineEdit}
          onCancelLineEdit={onCancelLineEdit}
          setEditValue={setEditValue}
          removeLineItem={removeLineItem}
          showAddParts={showAddParts}
          availableParts={availableParts}
          availableDeliveries={availableDeliveries}
          selectedDelivery={selectedDelivery}
          setSelectedDelivery={setSelectedDelivery}
          addPartsLoading={addPartsLoading}
          partQuantities={partQuantities}
          setPartQuantities={setPartQuantities}
          addingPart={addingPart}
          handleAddPart={handleAddPart}
          isSupplier={isSupplier}
          overByPart={overByPart}
        />
      </TabsContent>

      <TabsContent value="asn" className="m-0 p-4">
        <CallOffASNTab callOffId={callOff.id} onCountLoaded={setAsnTabCount} />
      </TabsContent>

      <TabsContent value="communication" className="m-0 p-4">
        <CallOffCommunicationTab
          messages={messages}
          isCustomer={isCustomer}
          isHaulier={isHaulier}
          detailStatus={callOff.detail_status}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          onSend={handleSendMessage}
          sending={sendingMessage}
        />
      </TabsContent>
    </Tabs>
  );
}
